import {
  isAuthError,
  isForbiddenError,
  isNotFoundError,
  isSuccess,
} from '@/context/services/type';
import type { FetchAdminInvoiceReportsArgs } from '@/features/admin_invoice_reports/types';
import { isCommonError, isObjectRecord } from '@/libs/typeguard/isError';
import { useMutation } from '@tanstack/react-query';
import type { SearchAdminInvoiceReportsCsvEncode } from 'ap-openapi';
import { useSearchAdminInvoiceReportsHook } from 'ap-openapi';

export { SearchAdminInvoiceReportsCsvEncode } from 'ap-openapi';

export type ExportAdminInvoiceReportsArgs = FetchAdminInvoiceReportsArgs & {
  csv_encode?: SearchAdminInvoiceReportsCsvEncode;
};

export enum AdminInvoiceReportExportStatus {
  SUCCESS = 'success',
  REDIRECT_TO_TEMPFILES = 'redirect_to_tempfiles',
  UNAUTHORIZED = 'unauthorized',
  FORBIDDEN = 'forbidden',
  NOT_FOUND = 'not_found',
  ERROR = 'error',
}

export type AdminInvoiceReportExportResult =
  | {
      status: AdminInvoiceReportExportStatus.SUCCESS;
      blob: Blob;
      message?: string;
    }
  | {
      status: AdminInvoiceReportExportStatus.REDIRECT_TO_TEMPFILES;
      redirectUrl: string;
    }
  | { status: AdminInvoiceReportExportStatus.UNAUTHORIZED }
  | {
      status: AdminInvoiceReportExportStatus.FORBIDDEN;
      message: string | undefined;
      code: string | undefined;
    }
  | { status: AdminInvoiceReportExportStatus.NOT_FOUND }
  | {
      status: AdminInvoiceReportExportStatus.ERROR;
      /** Parsed from blob when present; hook may still show generic toast per product rules */
      message: string | undefined;
    };

type BlobErrorPayload = { messages?: string[]; code?: string };

async function extractBlobError(data: unknown): Promise<BlobErrorPayload> {
  if (!(data instanceof Blob)) return {};
  try {
    const parsed: unknown = JSON.parse(await data.text());
    if (isObjectRecord(parsed) && isCommonError(parsed)) {
      return {
        messages: Array.isArray(parsed.messages) ? parsed.messages : undefined,
        code: typeof parsed.code === 'string' ? parsed.code : undefined,
      };
    }
  } catch (parseError: unknown) {
    // eslint-disable-next-line no-console
    console.error(
      'AdminInvoiceReportExport: failed to parse error blob as JSON',
      parseError
    );
  }
  return {};
}

const joinMessages = (messages: string[] | undefined): string | undefined =>
  messages?.length ? messages.join('\n') : undefined;

async function extractAsyncRedirectUrl(
  data: Blob
): Promise<string | undefined> {
  if (!data.type.toLowerCase().includes('application/json')) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(await data.text());
    if (!isObjectRecord(parsed) || typeof parsed.redirect_url !== 'string') {
      return undefined;
    }
    const url = parsed.redirect_url.trim();
    return url.startsWith('/tempfiles') ? url : undefined;
  } catch {
    return undefined;
  }
}

async function extractBlobMessage(data: Blob): Promise<string | undefined> {
  if (!data.type.toLowerCase().includes('application/json')) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(await data.text());
    if (!isObjectRecord(parsed)) return undefined;
    const msg =
      typeof parsed.message === 'string' ? parsed.message.trim() : undefined;
    return msg || undefined;
  } catch {
    return undefined;
  }
}

const BLOB_REQUEST_OPTIONS = {
  headers: { Accept: 'text/csv' },
  responseType: 'blob',
} as const;

type SearchResponse = { status: number; data: unknown };

/** Pure mapper: HTTP response → domain result (no UI side effects). */
export async function mapAdminInvoiceReportExportResponse(
  response: SearchResponse
): Promise<AdminInvoiceReportExportResult> {
  if (isSuccess(response.status) && response.data instanceof Blob) {
    const redirectUrl = await extractAsyncRedirectUrl(response.data);
    if (redirectUrl) {
      return {
        status: AdminInvoiceReportExportStatus.REDIRECT_TO_TEMPFILES,
        redirectUrl,
      };
    }
    const message = await extractBlobMessage(response.data);
    return {
      status: AdminInvoiceReportExportStatus.SUCCESS,
      blob: response.data,
      message,
    };
  }

  if (isAuthError(response.status)) {
    return { status: AdminInvoiceReportExportStatus.UNAUTHORIZED };
  }

  if (isForbiddenError(response.status)) {
    const { messages, code } = await extractBlobError(response.data);
    return {
      status: AdminInvoiceReportExportStatus.FORBIDDEN,
      message: joinMessages(messages),
      code,
    };
  }

  if (isNotFoundError(response.status)) {
    return { status: AdminInvoiceReportExportStatus.NOT_FOUND };
  }

  const { messages } = await extractBlobError(response.data);
  return {
    status: AdminInvoiceReportExportStatus.ERROR,
    message: joinMessages(messages),
  };
}

export function useExportAdminInvoiceReportsMutation() {
  const searchAdminInvoiceReports = useSearchAdminInvoiceReportsHook();

  return useMutation({
    mutationFn: async (
      args: ExportAdminInvoiceReportsArgs
    ): Promise<AdminInvoiceReportExportResult> => {
      const { page = 1, perPage = 50, query, order_by, csv_encode } = args;
      const response = await searchAdminInvoiceReports(
        {
          pagination: { per_page: perPage, page },
          query,
          order_by: order_by
            ? order_by.map((item) => ({
                field: item.field,
                direction: item.direction,
              }))
            : undefined,
        },
        { csv_encode },
        BLOB_REQUEST_OPTIONS
      );

      return mapAdminInvoiceReportExportResponse(response);
    },
  });
}
